import { KnownError } from "@/api/client";
import { AppFormLayout } from "@/components/app/app-form-layout";
import { AppWorkspaceLayout } from "@/components/app/app-workspace-layout";
import { appEnv } from "@/constants/app-env";
import { env } from "@/constants/env";
import { captureError } from "@/lib/errors/capture-error";
import { initializeGTM } from "@/lib/gtm";
import { initializeFBPixel } from "@/lib/track/pixel";
import { NotFound } from "@/routes/404";
import { CampaignNavigator } from "@/routes/campaign";
import { CampaignViewPage } from "@/routes/campaigns/[id]/campaign-view-page";
import { CampaignDetailPage } from "@/routes/campaigns/[id]/detail/campaign-detail-page.tsx";
import { CampaignsPage } from "@/routes/campaigns/campaigns-page";
import { MyCampaignsPage } from "@/routes/campaigns/my/my-campaigns";
import { CampaignViewsPage } from "@/routes/campaigns/views/[id]/campaign-view-page";
import { DSPAnalyticsPage } from "@/routes/custom-music/dsp-analytics/dsp-analytics-page";
import { CampaignFormPage } from "@/routes/form/campaign/form-campaign";
import { FormCampaignJoinPage } from "@/routes/form/campaign/join/form-campaign-join-page";
import { CampaignVideoSubmitPage } from "@/routes/form/campaign/submit/form-campaign-submit-page";
import { HomePage } from "@/routes/home/home-page";
import { JoinWorkspaceCallback } from "@/routes/join-workspace/callback/join-workspace-callback";
import { OauthCallbackTiktokPage } from "@/routes/oauth/callback/oauth-callback-tiktok-page";
import { WorkspaceNavigator } from "@/routes/workspace-navigator.tsx";
import { Toaster } from "@backpac/monica";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Route, useLocation } from "react-router";
import { AppAuthLayout } from "./components/app/app-auth-layout";
import { AuthBoundary } from "./components/shared/auth-boundary";
import { GlobalErrorBoundary } from "./components/shared/global-error-boundary";
import { routes } from "./constants/url";
import { SentryRoutes, initializeSentry } from "./lib/sentry.ts";
import { initializeMixpanel } from "./lib/track/mixpanel";
import { SupportAdminLayout } from "./routes/admin/support/components/support-admin-layout";
import { LegacySupportTicketRedirect } from "./routes/admin/support/legacy-ticket-redirect";
import { SupportFollowupsPage } from "./routes/admin/support/support-followups-page";
import { SupportInboxPage } from "./routes/admin/support/support-inbox-page";
import { SupportTicketDetailPage } from "./routes/admin/support/support-ticket-detail-page";
import { AuthCallbackPage } from "./routes/auth/callback/callback-page.tsx";
import { AuthErrorPage } from "./routes/auth/error/auth-error.tsx";
import { RequestResetPasswordPage } from "./routes/auth/request-reset-password/request-reset-password-page";
import { ResetPasswordPage } from "./routes/auth/reset-password/reset-password-page";
import { LoginPage } from "./routes/auth/sign-in/signin-page.tsx";
import { SignupEmailVerifyPage } from "./routes/auth/sign-up/email/verify/signup-email-verify-page.tsx";
import { SignUpPage } from "./routes/auth/sign-up/signup-page.tsx";
import { ChannelsPage } from "./routes/channels/channels-page";
import { JoinWorkspacePage } from "./routes/join-workspace/join-workspace.tsx";
import { DashboardPage } from "./routes/music/dashboard/dashboard-page.tsx";
import { MusicPage } from "./routes/music/music-page";
import { OauthCallbackPaypalPage } from "./routes/oauth/callback/oauth-callback-paypal-page.tsx";
import { PayoutPage } from "./routes/payout/payout-page";
import { SettingsAccountPage } from "./routes/settings/account/settings-account";
import { SettingsWorkspacePage } from "./routes/settings/workspace/settings-workspace.tsx";
import { SupportPage } from "./routes/support/support-page";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5,
      throwOnError: true, // NOTE: Catch errors in the nearby ErrorBoundary
    },
    mutations: {
      throwOnError: false, // NOTE: Catch errors in each mutation
      meta: {
        onError: (error: unknown) => {
          if (!KnownError.is(error)) {
            captureError(error);
          }
        },
      },
    },
  },
});

function App() {
  useEffect(() => {
    if (appEnv.isProduction) {
      initializeMixpanel();
      initializeSentry();
      initializeGTM();
      initializeFBPixel();
    }
  }, []);

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={env.GOOGLE_OAUTH_WEB_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <GlobalErrorBoundary>
            <AuthBoundary>
              <ScrollToTop />
              <SentryRoutes>
                <Route element={<AppWorkspaceLayout />}>
                  <Route path={routes.HOME.root} element={<HomePage />} />
                  <Route path={routes.CAMPAIGNS.root} element={<CampaignsPage />} />
                  <Route path={routes.CAMPAIGNS.my} element={<MyCampaignsPage />} />
                  <Route path={routes.CAMPAIGNS.detail} element={<CampaignDetailPage />} />
                  <Route path={routes.CAMPAIGNS.views} element={<CampaignViewsPage />} />
                  <Route path={routes.CAMPAIGNS.view} element={<CampaignViewPage />} />
                  <Route path={routes.MUSIC.root} element={<MusicPage />} />
                  <Route path={routes.MUSIC.dashboard} element={<DashboardPage />} />
                  <Route path={routes.PAYOUT.root} element={<PayoutPage />} />
                  <Route path={routes.CHANNELS.root} element={<ChannelsPage />} />
                  <Route path={routes.SETTINGS.workspace} element={<SettingsWorkspacePage />} />
                  <Route path={routes.SETTINGS.account} element={<SettingsAccountPage />} />

                  <Route path={routes.CUSTOM_MUSIC.dspAnalytics} element={<DSPAnalyticsPage />} />
                </Route>

                <Route path={routes.AUTH.root} element={<AppAuthLayout />}>
                  <Route path={routes.AUTH.signin} element={<LoginPage />} />
                  <Route path={routes.AUTH.signup}>
                    <Route index element={<SignUpPage />} />
                    <Route path={routes.AUTH.signupEmailVerify} element={<SignupEmailVerifyPage />} />
                  </Route>
                  <Route path={routes.AUTH.resetPassword} element={<ResetPasswordPage />} />
                  <Route path={routes.AUTH.requestResetPassword} element={<RequestResetPasswordPage />} />
                </Route>

                <Route path={routes.AUTH.callback} element={<AuthCallbackPage />} />
                <Route path={routes.AUTH.error} element={<AuthErrorPage />} />
                <Route path={routes.OAUTH.paypal} element={<OauthCallbackPaypalPage />} />
                <Route path={routes.OAUTH.tiktok} element={<OauthCallbackTiktokPage />} />
                <Route path={routes.JOIN_WORKSPACE.root} element={<JoinWorkspacePage />} />
                <Route path={routes.JOIN_WORKSPACE.callback} element={<JoinWorkspaceCallback />} />
                <Route element={<AppFormLayout />}>
                  <Route path={routes.CAMPAIGNS.form} element={<CampaignFormPage />} />
                  <Route path={routes.CAMPAIGNS.videoSubmit} element={<CampaignVideoSubmitPage />} />
                  <Route path={routes.CAMPAIGNS.join} element={<FormCampaignJoinPage />} />
                </Route>
                {/* NOTE(internal-customer-support): Public, unauthenticated contact form.
                    Deliberately outside <AppWorkspaceLayout /> (no workspace requirement)
                    and outside <AppFormLayout /> (that layout adds a "back" close button meant
                    for in-app multi-step forms, not a standalone public marketing-style page). */}
                <Route path="/support" element={<SupportPage />} />

                {/* NOTE(internal-customer-support): Staff inbox. Outside <AppWorkspaceLayout />
                    since it has no :workspaceId and isn't workspace-scoped data. Auth is
                    enforced by <SupportAdminLayout /> (requires a logged-in session; see that
                    file for why <AuthBoundary /> itself did not need to change) plus the
                    server-side staff check on every /admin/support/* call. */}
                <Route path="/admin/support" element={<SupportAdminLayout />}>
                  <Route index element={<SupportInboxPage />} />
                  <Route path="followups" element={<SupportFollowupsPage />} />
                  <Route path=":id" element={<SupportTicketDetailPage />} />
                  <Route path="tickets/:id" element={<LegacySupportTicketRedirect />} />
                </Route>

                <Route path={routes.NOT_FOUND.root} element={<NotFound />} />
                <Route path={routes.WORKSPACE_NAVIGATOR.campaigns} element={<CampaignNavigator />} />
                <Route path="*" element={<WorkspaceNavigator />} />
              </SentryRoutes>
              <Toaster />
            </AuthBoundary>
          </GlobalErrorBoundary>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;

// @see https://v5.reactrouter.com/web/guides/scroll-restoration
function ScrollToTop() {
  const { pathname } = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
